/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import { Assignment, ExitToApp } from '@mui/icons-material';
import Avatar from '@mui/material/Avatar';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import * as Colors from '@mui/material/colors';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { withUser } from '../../UserProvider';
import { useLocale } from '../../languages/LocaleProvider';

function UserMenuComponent({ user }) {
	const { locale } = useLocale();
	const [anchorEl, setAnchorEl] = useState(null);
	const navigate = useNavigate();

	const getLocale = () => {
		return locale || 'EN';
	};

	const showLicenseAgreement = () => {
		window.open(`/license_${getLocale().toUpperCase()}.pdf`);
	};

	const logout = () => {
		navigate('/logout');
	};

	return (
		<div
			style={{
				marginLeft: '20px'
			}}
		>
			<Tooltip
				enterDelay={300}
				title={<FormattedMessage id="Tooltip.SettingsMenu" defaultMessage="User Settings and Info" />}
			>
				<div>
					<IconButton
						aria-label="More"
						color="default"
						aria-haspopup="true"
						onClick={(event) => {
							setAnchorEl(event.currentTarget);
						}}
						size="large"
					>
						<Avatar sx={{ width: 32, height: 32 }} at="User">
							{user && user.displayName && user.displayName.length ? user.displayName[0] : '?'}
						</Avatar>
					</IconButton>
				</div>
			</Tooltip>
			{anchorEl ? (
				<Menu id="long-menu" anchorEl={anchorEl} open={anchorEl !== null} onClose={() => setAnchorEl(null)}>
					<Card
						square
						elevation={0}
						style={{
							marginTop: '-8px',
							outline: 'none'
						}}
					>
						<CardHeader
							title={
								<div
									style={{
										color: (theme) =>
											`${theme.components.MuiAppBar.styleOverrides.colorIcon.backgroundColor}, 1)`
									}}
								>
									{user.displayName}
								</div>
							}
							subheader={<address style={{ color: Colors.grey[50] }}>{user ? user.mail : ''}</address>}
							avatar={<Avatar sx={{ width: 24, height: 24 }} alt="User" src="images/avatar.png" />}
							disabled
							style={{
								backgroundColor: (theme) =>
									theme.components.MuiAppBar.styleOverrides.colorPrimary.backgroundColor,
								outline: 'none'
							}}
						/>
					</Card>
					<MenuItem dense onClick={() => showLicenseAgreement(getLocale().toUpperCase())}>
						<ListItemIcon>
							<Assignment />
						</ListItemIcon>
						<ListItemText
							primary={
								<FormattedMessage
									id="Setup.LicenseAgreement.DownloadLicense"
									defaultMessage="Download license"
								/>
							}
						/>
					</MenuItem>
					<Divider />
					<MenuItem dense onClick={() => logout()}>
						<ListItemIcon>
							<ExitToApp />
						</ListItemIcon>
						<ListItemText primary={<FormattedMessage id="Logout" defaultMessage="Logout" />} />
					</MenuItem>
				</Menu>
			) : null}
		</div>
	);
}

export const UserMenu = withUser(({ user }) => ({ user }))(UserMenuComponent);
